/**
 * The console client of the site-admin kernel-rights routes of release-engine (kernel apps, WS-9a,
 * `design-docs/WS-9.md` section 3.1): the queue and the decision.
 *
 * These two routes sit behind `cp:kernel-rights:decide`, a site-wide permission (D8 of
 * 2026-10-20-cp-permissioning; it replaced the marker `cp:platform:admin`), and the app routes of
 * `kernelRights.api.ts` sit behind `cp:app:read` on one app. The two halves live in two modules, so
 * that each pane imports the module whose permission gates it (`permission-gate-parity.test.ts`).
 *
 * Who calls it: `KernelRightsQueuePane` and `KernelRightDecisionPanel`. The decision goes through
 * `useStepUp()`. Every call keeps the rules of `kernelRights.api.ts`: an encoded path id, the
 * step-up header from `stepUpHeaders`, the WS-7 body keys alone, `ApiError` on a non-2xx answer,
 * and no retry, cache or store.
 */
import type { CpPermission } from '@zion/sdk-contracts';
import type { KernelRightRequestView } from './kernelRights.api';
import { stepUpHeaders } from '../platform/auth/step-up';
import { api, apiFetch } from '../platform/http/client';

/**
 * The check of the queue and of the decision: `cp:kernel-rights:decide`, site-wide (D8).
 * `platform_admin` holds it. Segment 2 is no member of `GRANT_RESOURCE_TYPES`, so no app grant
 * carries it.
 */
export const KERNEL_RIGHTS_DECIDE_PERMISSION = 'cp:kernel-rights:decide' satisfies CpPermission;

/** The four states of a kernel-right request, in the order of the queue segments. */
export const KERNEL_RIGHT_REQUEST_STATUSES = ['pending', 'approved', 'denied', 'revoked'] as const;

/** One state of a kernel-right request. `pending` moves to `approved` or `denied`; `approved` moves to `revoked`. */
export type KernelRightRequestStatus = (typeof KERNEL_RIGHT_REQUEST_STATUSES)[number];

/** The page size that the console asks of the queue: the largest that WS-7's query admits. */
export const KERNEL_RIGHT_QUEUE_LIMIT = 200;

/** One entry of the site-admin queue: the request with the slug and the name of its app. */
export interface KernelRightQueueEntry extends KernelRightRequestView {
  readonly appSlug: string;
  readonly appName: string;
}

/**
 * The body of the decision route. An approve may omit the note; a deny and a revoke must carry one.
 *
 * `globalBucketLocation` sits on the approve arm alone (plan 2026-10-13-blob-buckets, R-094 item
 * 3). It is one single cloud region in the grammar of `dataLocationSchema`, for example
 * `europe-west1`. release-engine stores it in `release.kernel_right_requests.global_bucket_location`,
 * and every shared (`scope: global`) bucket of the app lives at that location in the control plane
 * project. It must be present on an approve of a request whose rights name `storage.global`, and
 * absent on every other decision. A deny or a revoke cannot carry it: the type has no such key.
 */
export type KernelRightDecisionBody =
  | { readonly decision: 'approve'; readonly note?: string; readonly globalBucketLocation?: string }
  | { readonly decision: 'deny' | 'revoke'; readonly note: string };

/**
 * The refusal code of an approve of a `storage.global` request with no `globalBucketLocation`
 * (R-094 item 3). The console expects status 400 with this code in the `error` key.
 */
export const GLOBAL_BUCKET_LOCATION_REQUIRED = 'global_bucket_location_required';

/**
 * The refusal code of a decision that carries `globalBucketLocation` for a request whose rights do
 * not name `storage.global` (R-094 item 3). The console expects status 400 with this code in the
 * `error` key. A deny or a revoke with the key gets the pipe's 400 first, because only the approve
 * arm of the body schema admits it.
 */
export const GLOBAL_BUCKET_LOCATION_REFUSED = 'global_bucket_location_refused';

/**
 * The refusal code of an approve whose `globalBucketLocation` is outside the locations that the
 * platform offers for a shared bucket: the entries of release-engine's `ZION_DP_ALLOWED_REGIONS`,
 * else its five default regions (R-095 item 3). A location of a shared bucket that the app already
 * has is no refusal of this code (R-097 item 2). The console expects status 400 with this code in
 * the `error` key. The request stays `pending`, and no step-up opens.
 */
export const GLOBAL_BUCKET_LOCATION_NOT_ALLOWED = 'global_bucket_location_not_allowed';

/**
 * The refusal code of an approve whose `globalBucketLocation` differs from the location of a shared
 * bucket that the app already has (R-095 item 3). A bucket location never changes, so the approve
 * must name the location of those buckets. The console expects status 409 with this code in the
 * `error` key and that location in {@link GLOBAL_BUCKET_FIXED_LOCATION_KEY}. The request stays
 * `pending`, and no step-up opens.
 */
export const GLOBAL_BUCKET_LOCATION_FIXED = 'global_bucket_location_fixed';

/**
 * The body key of a {@link GLOBAL_BUCKET_LOCATION_FIXED} refusal that names a location of the
 * shared buckets of the app: the first location in text order that differs from the requested one.
 */
export const GLOBAL_BUCKET_FIXED_LOCATION_KEY = 'fixedLocation';

/**
 * The largest offset that WS-7's queue query admits (`kernelRightQueueQuerySchema`, 0 to 10,000).
 * So the console reads no entry after entry 10,200 of one status.
 */
export const KERNEL_RIGHT_QUEUE_MAX_OFFSET = 10_000;

/**
 * The path of one queue page: `/v1/admin/kernel-rights/requests?status=<status>&limit=200`, and
 * `&offset=<offset>` when the offset is above 0. Pure.
 *
 * @param offset the count of entries before the page, an integer from 0 to
 *   {@link KERNEL_RIGHT_QUEUE_MAX_OFFSET}. The default 0 is the first page.
 * @throws Error('invalid queue offset') for any other offset, so the console never sends a query
 *   that the WS-7 pipe refuses with 400.
 */
export function kernelRightQueuePath(status: KernelRightRequestStatus, offset = 0): string {
  if (!Number.isInteger(offset) || offset < 0 || offset > KERNEL_RIGHT_QUEUE_MAX_OFFSET) {
    throw new Error('invalid queue offset');
  }
  const query = new URLSearchParams({ status, limit: String(KERNEL_RIGHT_QUEUE_LIMIT) });
  if (offset > 0) query.set('offset', String(offset));
  return `/v1/admin/kernel-rights/requests?${query.toString()}`;
}

/**
 * Read one page of the site-admin queue in one status: at most {@link KERNEL_RIGHT_QUEUE_LIMIT}
 * entries, oldest first, after the first `offset` entries (WS-7 `KernelRightsAdminController.queue`).
 *
 * @param offset as {@link kernelRightQueuePath}; the default 0 reads the first page.
 * @throws Error('invalid queue offset') before any request for an offset out of range; ApiError on
 *   a non-2xx answer (403 with no `cp:kernel-rights:decide`).
 */
export async function listKernelRightQueue(
  status: KernelRightRequestStatus,
  offset = 0,
): Promise<readonly KernelRightQueueEntry[]> {
  return api.get<readonly KernelRightQueueEntry[]>(kernelRightQueuePath(status, offset));
}

/**
 * The wire form of a decision body: `decision`, then `note` when the body holds one, then
 * `globalBucketLocation` when the approve arm holds one. No other key reaches the wire, so a body
 * object with an extra run-time key cannot widen the request. Pure.
 */
function decisionWire(body: KernelRightDecisionBody): Readonly<Record<string, string>> {
  const wire: Record<string, string> = { decision: body.decision };
  if (body.note !== undefined) wire['note'] = body.note;
  if (body.decision === 'approve' && body.globalBucketLocation !== undefined) wire['globalBucketLocation'] = body.globalBucketLocation;
  return wire;
}

/**
 * Approve, deny or revoke a request: `POST /v1/admin/kernel-rights/requests/:id/decision`.
 * The body is {@link decisionWire} of `body`: `decision`, `note` only when the caller gave one, and
 * `globalBucketLocation` only on an approve that carries one.
 *
 * @param stepUpId `null` on the first send; the approved step-up request id on the second.
 * @returns the request after the decision (200).
 * @throws Error('invalid step-up request id') before any request for a malformed id; ApiError on a
 *   non-2xx answer: 400 from the pipe, 400 {@link GLOBAL_BUCKET_LOCATION_REQUIRED} for an approve of
 *   a `storage.global` request with no location, 400 {@link GLOBAL_BUCKET_LOCATION_REFUSED} for a
 *   location on a request without `storage.global`, 400 {@link GLOBAL_BUCKET_LOCATION_NOT_ALLOWED}
 *   for a location that the platform does not offer and no shared bucket of the app has, 409
 *   {@link GLOBAL_BUCKET_LOCATION_FIXED} for a
 *   location other than that of the app's shared buckets, 403 `step_up_required` on a first send, 403
 *   `self_decision_refused` for an approve by the asker, 404 for an unknown request, 409 for a
 *   request in another state, 503 `code_signing_unavailable` when the custodian cannot create the
 *   signing key.
 */
export async function decideKernelRightRequest(
  requestId: string,
  body: KernelRightDecisionBody,
  stepUpId: string | null,
): Promise<KernelRightRequestView> {
  const wire = decisionWire(body);
  return apiFetch<KernelRightRequestView>(`/v1/admin/kernel-rights/requests/${encodeURIComponent(requestId)}/decision`, {
    method: 'POST',
    body: JSON.stringify(wire),
    headers: stepUpHeaders(stepUpId),
  });
}
